import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface SetupData {
  email: string;
  totp_secret: string;
  provisioning_uri: string;
  qr_code_data_url?: string;
}

type FatalError = 'invalid_or_expired_token' | 'studi_email_conflict' | 'load_failed';

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold">
            FOM
          </div>
          <div>
            <div className="font-semibold text-slate-900">FOM Hochschule</div>
            <div className="text-xs text-slate-500">Portal-Zugang einrichten</div>
          </div>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}

function FatalErrorView({ kind }: { kind: FatalError }) {
  const messages: Record<FatalError, { title: string; body: string }> = {
    invalid_or_expired_token: {
      title: 'Setup-Link ungültig oder abgelaufen',
      body:
        'Dieser Einrichtungs-Link ist nicht mehr gültig. Bitte fordern Sie über die FOM-Hochschulverwaltung einen neuen Link an.',
    },
    studi_email_conflict: {
      title: 'E-Mail-Adresse gehört zu einem Studierenden',
      body:
        'Diese E-Mail-Adresse ist im System einem aktiven Studierenden zugeordnet und kann daher nicht als Zuzahler-Zugang verwendet werden. Bitte wenden Sie sich an die FOM-Hochschulverwaltung.',
    },
    load_failed: {
      title: 'Verbindungsproblem',
      body:
        'Die Setup-Daten konnten nicht geladen werden. Bitte versuchen Sie es in wenigen Minuten erneut.',
    },
  };
  const m = messages[kind];
  return (
    <Layout>
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8">
        <h1 className="text-xl font-bold text-red-700 mb-2">{m.title}</h1>
        <p className="text-sm text-slate-700">{m.body}</p>
      </div>
    </Layout>
  );
}

export default function SetupPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<SetupData | null>(null);
  const [fatal, setFatal] = useState<FatalError | null>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setFatal('invalid_or_expired_token');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/zuzahler/setup/${encodeURIComponent(token)}`, {
          method: 'GET',
          credentials: 'include',
        });
        if (resp.status === 404) {
          if (!cancelled) setFatal('invalid_or_expired_token');
          return;
        }
        if (resp.status === 409) {
          if (!cancelled) setFatal('studi_email_conflict');
          return;
        }
        if (!resp.ok) {
          if (!cancelled) setFatal('load_failed');
          return;
        }
        const json = (await resp.json()) as SetupData;
        if (!cancelled) setData(json);
      } catch (err) {
        console.error('Setup load failed', err);
        if (!cancelled) setFatal('load_failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !code.trim() || submitting) return;
    setSubmitting(true);
    setCodeError(null);
    try {
      const resp = await fetch(`/api/zuzahler/setup/${encodeURIComponent(token)}/totp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (resp.status === 401) {
        setCodeError('Falscher Code. Bitte erneut versuchen.');
        return;
      }
      if (resp.status === 404) {
        setFatal('invalid_or_expired_token');
        return;
      }
      if (!resp.ok) {
        setCodeError('Ein unerwarteter Fehler ist aufgetreten. Bitte erneut versuchen.');
        return;
      }
      const json = await resp.json();
      navigate(json.redirect || '/zuzahler/tasks', { replace: true });
    } catch (err) {
      console.error('Setup submit failed', err);
      setCodeError('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  }

  if (fatal) {
    return <FatalErrorView kind={fatal} />;
  }

  if (!data) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
        <h1 className="text-xl font-bold text-slate-900 mb-2">
          Authenticator einrichten
        </h1>
        <p className="text-sm text-slate-600 mb-6">
          Wir haben einen Portal-Zugang für{' '}
          <span className="font-medium text-slate-900">{data.email}</span> vorbereitet.
          Bitte richten Sie jetzt Ihre Authenticator-App ein — damit sichern Sie
          Ihren Zugang mit einem zweiten Faktor.
        </p>

        <ol className="text-sm text-slate-700 list-decimal pl-5 space-y-2 mb-6">
          <li>
            Öffnen Sie eine Authenticator-App (z. B. Microsoft Authenticator,
            Google Authenticator, Authy).
          </li>
          <li>Scannen Sie den QR-Code oder fügen Sie den Schlüssel manuell hinzu.</li>
          <li>Geben Sie den 6-stelligen Code aus der App unten ein.</li>
        </ol>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-center">
          {data.qr_code_data_url ? (
            <img
              src={data.qr_code_data_url}
              alt="QR-Code für Authenticator-Setup"
              className="mx-auto w-48 h-48"
            />
          ) : (
            <div className="text-xs text-slate-600 break-all">
              <div className="mb-2 font-medium">Provisioning-URI:</div>
              <code>{data.provisioning_uri}</code>
            </div>
          )}
          <div className="mt-4 text-xs text-slate-500">
            Schlüssel manuell:{' '}
            <code className="font-mono text-slate-800">{data.totp_secret}</code>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="totp-code"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              6-stelliger Code aus der Authenticator-App
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="123456"
              disabled={submitting}
              required
            />
          </div>

          {codeError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {codeError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold rounded-lg px-4 py-2 transition-colors"
          >
            {submitting ? 'Wird geprüft …' : 'Einrichtung abschließen'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
